<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-700">
        {{ phoneNumbers.length }} phone number{{ phoneNumbers.length !== 1 ? 's' : '' }}
      </h3>
      <button
        class="text-xs text-gray-500 hover:text-gray-700"
        :disabled="loading"
        @click="fetchData"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="mr-1" />
        Refresh
      </button>
    </div>

    <p data-test="support-banner" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
      To reassign, unassign, deactivate or delete a phone number, email
      <a href="mailto:support@kaironhealth.com" class="underline">support@kaironhealth.com</a>.
    </p>

    <!-- Approved area codes: tenant admins, security and superusers edit them. -->
    <div data-test="area-codes" class="mb-4 p-4 border border-gray-200 rounded-lg">
      <div class="text-xs font-medium text-gray-700 mb-2">Approved area codes</div>
      <div class="flex flex-wrap gap-2 items-center">
        <span
          v-for="code in (canManage ? areaCodesDraft : approvedAreaCodes)"
          :key="code"
          data-test="area-code-chip"
          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
          :class="areaCodeErrorFor(code) ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'"
        >
          {{ code }}
          <button
            v-if="canManage"
            data-test="remove-area-code"
            class="ml-1 text-gray-500 hover:text-gray-800"
            :aria-label="`Remove ${code}`"
            @click="removeAreaCode(code)"
          >
            ×
          </button>
        </span>
        <span v-if="!(canManage ? areaCodesDraft : approvedAreaCodes).length" class="text-xs text-gray-500">
          None yet.{{ canManage ? ' Add the area codes your organization can buy numbers in.' : '' }}
        </span>
      </div>

      <div v-if="canManage" data-test="area-code-editor" class="mt-3 flex gap-2 items-center">
        <input
          v-model="newAreaCode"
          data-test="new-area-code"
          type="text"
          maxlength="3"
          placeholder="312"
          aria-label="Area code to add"
          class="text-sm border border-gray-300 rounded-md px-2 py-1 w-20"
          @keydown.enter.prevent="addAreaCode"
        >
        <button class="text-sm px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50" @click="addAreaCode">
          Add
        </button>
        <button
          data-test="save-area-codes"
          class="text-sm px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
          :disabled="savingConfig || !areaCodesDirty"
          @click="saveAreaCodes"
        >
          Save
        </button>
      </div>
      <ul v-if="areaCodeErrors.length" data-test="area-code-errors" class="mt-2 text-xs text-red-700 list-disc ml-4">
        <li v-for="e in areaCodeErrors" :key="`${e.code}-${e.reason}`">
          {{ e.code }}: {{ e.reason === 'duplicate' ? 'listed more than once' : 'not a valid US area code' }}
        </li>
      </ul>
    </div>

    <!-- Purchase: tenant admins/security and superusers. -->
    <div v-if="canManage" data-test="purchase-panel" class="mb-4 p-4 border border-gray-200 rounded-lg space-y-3">
      <p
        v-if="!purchaseEnabled"
        data-test="purchase-disabled-banner"
        class="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800"
      >
        Purchasing phone numbers is disabled in this environment. It is only available in production.
      </p>

      <div v-if="!addOpen">
        <button
          data-test="add-number"
          class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
          :disabled="approvedAreaCodes.length === 0"
          @click="openAdd"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
          Add number
        </button>
        <span v-if="approvedAreaCodes.length === 0" class="ml-2 text-xs text-gray-500">
          No approved area codes yet.
        </span>
      </div>

      <div v-else data-test="add-form" class="space-y-3">
        <div class="flex gap-2 items-end">
          <div>
            <label for="add-area-code" class="block text-xs font-medium text-gray-700 mb-1">Area code</label>
            <select id="add-area-code" v-model="search.areaCode" class="text-sm border border-gray-300 rounded-md px-2 py-1">
              <option v-for="code in approvedAreaCodes" :key="code" :value="code">{{ code }}</option>
            </select>
          </div>
          <button
            data-test="search-numbers"
            class="text-sm px-3 py-1 rounded-md border border-gray-300 hover:bg-gray-50"
            :disabled="search.loading"
            @click="runSearch"
          >
            Search
          </button>
        </div>

        <p v-if="search.notice" data-test="search-notice" class="text-xs text-orange-700">{{ search.notice }}</p>

        <div v-if="search.results.length">
          <label for="add-number-choice" class="block text-xs font-medium text-gray-700 mb-1">Number</label>
          <select id="add-number-choice" v-model="purchase.phoneNumber" class="text-sm border border-gray-300 rounded-md px-2 py-1 w-full">
            <option v-for="n in search.results" :key="n.phone_number" :value="n.phone_number">
              {{ formatPhone(n.phone_number) }}{{ placeLabel(n) ? ` — ${placeLabel(n)}` : '' }}
            </option>
          </select>
        </div>
        <p v-else-if="search.done && search.searchedCode" data-test="search-empty" class="text-xs text-gray-600">
          Twilio has no numbers available in area code {{ search.searchedCode }} right now. This is common in busy
          area codes; try another approved area code, or try again later.
        </p>

        <div v-if="search.results.length" data-test="assignee-picker">
          <FilterDropdown
            v-model="purchase.userId"
            label="Assign to"
            :options="userOptions"
            placeholder="Select a user"
            search-placeholder="Search users…"
            searchable
            sensitive-labels
            full-width
            class="w-full"
          />
        </div>

        <div class="flex gap-2">
          <button
            data-test="review-purchase"
            class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            :disabled="!purchase.phoneNumber || !purchase.userId || !purchaseEnabled"
            @click="confirmOpen = true"
          >
            Review purchase
          </button>
          <button class="text-sm px-3 py-1 rounded-md border border-gray-300" @click="closeAdd">Cancel</button>
        </div>
      </div>
    </div>

    <!-- Confirmation: nothing is bought until the user confirms here. -->
    <div
      v-if="confirmOpen"
      data-test="confirm-dialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-purchase-title"
    >
      <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md space-y-3">
        <h4 id="confirm-purchase-title" class="text-base font-semibold text-gray-900">Buy this phone number?</h4>
        <dl class="text-sm grid grid-cols-3 gap-y-1">
          <dt class="text-gray-500">Number</dt>
          <dd data-test="confirm-number" class="col-span-2 sensitive">{{ formatPhone(purchase.phoneNumber) }}{{ selectedLocation }}</dd>
          <dt class="text-gray-500">Organization</dt>
          <dd data-test="confirm-tenant" class="col-span-2">{{ current_tenant_name }}</dd>
          <dt class="text-gray-500">Assigned to</dt>
          <dd data-test="confirm-assignee" class="col-span-2 sensitive">{{ selectedUserLabel }}</dd>
        </dl>
        <p class="text-sm text-orange-800 bg-orange-50 border border-orange-200 rounded p-2">
          This purchase costs money: the number is billed monthly to your organization's Twilio account.
        </p>
        <div class="flex justify-end gap-2">
          <button
            data-test="cancel-purchase"
            class="text-sm px-3 py-1 rounded-md border border-gray-300"
            :disabled="purchase.loading"
            @click="confirmOpen = false"
          >
            Cancel
          </button>
          <button
            data-test="confirm-purchase"
            class="text-sm px-3 py-1 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50"
            :disabled="purchase.loading"
            @click="confirmPurchase"
          >
            Buy number
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <div v-else-if="phoneNumbers.length === 0" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">No phone numbers configured</p>
    </div>

    <div v-else class="header-wrap-table">
      <AgGridVue
        :theme="standardListTheme"
        style="height: 500px"
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="phoneNumbers"
      />
    </div>

    <div v-if="error" data-test="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { standardListTheme } from '@/utils/common'
import { mapGetters } from 'vuex'
import {
  listAdminPhoneNumbers,
  getTelephonyConfig,
  updateTelephonyConfig,
  searchAvailablePhoneNumbers,
  purchasePhoneNumber,
  telephonyError,
} from '@/services/api/tenantPhoneNumbers'
import { listUsers } from '@/services/api/adminUsers'
import FilterDropdown from '@/components/widgetcomponents/library/FilterDropdown.vue'

export default {
  name: 'TelephonyPhoneNumbers_Admin',
  components: { AgGridVue, FontAwesomeIcon, FilterDropdown },

  data() {
    return {
      phoneNumbers: [],
      users: [],
      loading: true,
      error: null,
      approvedAreaCodes: [],
      purchaseEnabled: false,
      areaCodesDraft: [],
      newAreaCode: '',
      areaCodeErrors: [],
      savingConfig: false,
      addOpen: false,
      confirmOpen: false,
      search: { areaCode: null, results: [], loading: false, done: false, notice: null, searchedCode: null },
      purchase: { phoneNumber: null, userId: null, loading: false },
      standardListTheme,
      defaultColDef: {
        sortable: true,
        resizable: true,
        suppressMovable: true,
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_name', 'isTenantAdmin', 'isSecurityUser']),

    isSuperuser() {
      return Boolean(this.current_user?.is_superuser)
    },

    // Mirrors the backend's IsTenantAdminSecurityOrSuperuser; the API is the authority.
    canManage() {
      return this.isSuperuser || this.isTenantAdmin || this.isSecurityUser
    },

    areaCodesDirty() {
      return this.areaCodesDraft.join(',') !== this.approvedAreaCodes.join(',')
    },

    userOptions() {
      return this.users.map((u) => ({
        label: `${u.first_name} ${u.last_name}`.trim() || u.email,
        value: u.id,
      }))
    },

    selectedUserLabel() {
      return this.userOptions.find((o) => o.value === this.purchase.userId)?.label || ''
    },

    selectedLocation() {
      const n = this.search.results.find((r) => r.phone_number === this.purchase.phoneNumber)
      const place = n ? this.placeLabel(n) : ''
      return place ? ` (${place})` : ''
    },

    columnDefs() {
      const self = this
      return [
        {
          headerName: 'Phone Number',
          field: 'phone_number',
          cellClass: 'sensitive',
          valueFormatter: (params) => self.formatPhone(params.value),
          flex: 1,
          minWidth: 160,
        },
        {
          headerName: 'Friendly Name',
          field: 'friendly_name',
          flex: 1,
          minWidth: 150,
        },
        {
          headerName: 'Status',
          field: 'status',
          width: 120,
          cellRenderer: (params) => {
            const status = params.value || 'unknown'
            const badgeClass = status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            const el = document.createElement('span')
            el.className = `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass}`
            el.textContent = status
            return el
          },
        },
        {
          headerName: 'Assigned User',
          field: 'assigned_user_name',
          cellClass: 'sensitive',
          flex: 1,
          minWidth: 200,
          valueFormatter: (params) => params.value || 'Unassigned',
        },
        {
          headerName: 'Voice',
          field: 'voice_enabled',
          width: 90,
          valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
          headerName: 'SMS',
          field: 'sms_enabled',
          width: 90,
          valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
        },
        {
          headerName: 'Twilio SID',
          field: 'twilio_sid',
          minWidth: 160,
          valueFormatter: (params) => params.value || 'Not in Twilio',
        },
        {
          headerName: 'Added',
          field: 'created_at',
          width: 120,
          sort: 'desc',
          valueFormatter: (params) => self.formatDate(params.value),
        },
      ]
    },
  },

  mounted() {
    this.fetchData()
  },

  methods: {
    async fetchData() {
      this.loading = true
      this.error = null
      try {
        const [phones, users, config] = await Promise.all([
          listAdminPhoneNumbers(),
          this.canManage ? listUsers() : Promise.resolve([]),
          getTelephonyConfig(),
        ])
        this.phoneNumbers = phones
        this.users = users
        this.applyConfig(config)
      } catch {
        this.error = 'Failed to load phone numbers'
      } finally {
        this.loading = false
      }
    },

    applyConfig(config) {
      this.approvedAreaCodes = config.approved_area_codes || []
      this.areaCodesDraft = [...this.approvedAreaCodes]
      this.purchaseEnabled = Boolean(config.purchase_enabled)
      this.areaCodeErrors = []
    },

    errorMessage(err, fallback) {
      return telephonyError(err)?.message || fallback
    },

    areaCodeErrorFor(code) {
      return this.areaCodeErrors.find((e) => e.code === code)
    },

    addAreaCode() {
      const code = this.newAreaCode.trim()
      if (!code) return
      // Duplicates and invalid codes are sent as-is so the API reports them per code.
      this.areaCodesDraft = [...this.areaCodesDraft, code]
      this.newAreaCode = ''
    },

    removeAreaCode(code) {
      const idx = this.areaCodesDraft.lastIndexOf(code)
      if (idx !== -1) this.areaCodesDraft = this.areaCodesDraft.filter((_, i) => i !== idx)
      this.areaCodeErrors = this.areaCodeErrors.filter((e) => e.code !== code)
    },

    async saveAreaCodes() {
      this.savingConfig = true
      this.error = null
      try {
        const config = await updateTelephonyConfig({ approved_area_codes: this.areaCodesDraft })
        this.applyConfig(config)
      } catch (err) {
        const apiErr = telephonyError(err)
        if (apiErr?.code === 'invalid_area_codes') {
          this.areaCodeErrors = apiErr.details || []
        } else {
          this.error = this.errorMessage(err, 'Failed to save approved area codes')
        }
      } finally {
        this.savingConfig = false
      }
    },

    openAdd() {
      this.addOpen = true
      this.search = {
        areaCode: this.approvedAreaCodes[0],
        results: [],
        loading: false,
        done: false,
        notice: null,
        searchedCode: null,
      }
      this.purchase = { phoneNumber: null, userId: null, loading: false }
    },

    closeAdd() {
      this.addOpen = false
      this.confirmOpen = false
    },

    async runSearch() {
      this.search.loading = true
      this.search.notice = null
      this.error = null
      try {
        this.search.searchedCode = this.search.areaCode
        this.search.results = await searchAvailablePhoneNumbers(this.search.areaCode)
        this.purchase.phoneNumber = this.search.results[0]?.phone_number || null
      } catch (err) {
        this.search.results = []
        this.search.searchedCode = null
        this.error = this.errorMessage(err, 'Failed to search for numbers')
      } finally {
        this.search.loading = false
        this.search.done = true
      }
    },

    async confirmPurchase() {
      this.purchase.loading = true
      this.error = null
      try {
        await purchasePhoneNumber({ phone_number: this.purchase.phoneNumber, user_id: this.purchase.userId })
        this.confirmOpen = false
        this.addOpen = false
        await this.fetchData()
      } catch (err) {
        this.confirmOpen = false
        if (telephonyError(err)?.code === 'number_unavailable') {
          this.search.results = []
          this.search.done = false
          this.purchase.phoneNumber = null
          this.search.notice = 'That number is no longer available. Search again to pick another.'
        } else {
          this.error = this.errorMessage(err, 'Failed to buy number')
        }
      } finally {
        this.purchase.loading = false
      }
    },

    // Twilio often omits locality and returns region "US"; fall back to the rate center when it reads
    // like a city name. Rate centers such as "CHICGOZN05" are carrier codes, not places, so skip them.
    placeLabel(n) {
      const rateCenterName = n.rate_center && !/\d/.test(n.rate_center) ? this.titleCase(n.rate_center) : ''
      const city = n.locality || rateCenterName
      const region = n.region && n.region !== 'US' ? n.region : ''
      return [city, region].filter(Boolean).join(', ')
    },

    titleCase(value) {
      if (!value) return ''
      return value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
    },

    formatDate(value) {
      return value ? new Date(value).toLocaleDateString() : ''
    },

    formatPhone(phone) {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
  },
}
</script>
